/**
 * Date helpers for HTML form inputs.
 */

/**
 * Convert a UTC ISO timestamp (as returned by the API — `TIMESTAMPTZ` columns
 * serialize as e.g. "2026-08-07T19:00:00.000Z") into the `YYYY-MM-DDTHH:mm`
 * string that an `<input type="datetime-local">` expects.
 *
 * WHY THIS EXISTS: a `datetime-local` value carries NO timezone — the browser
 * reads it as local wall-clock time. Seeding such an input by slicing the UTC
 * string (`iso.slice(0, 16)`) therefore displays the UTC clock time as if it
 * were local, and on save `new Date(localString).toISOString()` re-encodes it
 * as a *different instant*, shifting the stored timestamp by the UTC offset —
 * cumulatively, once per edit-and-save, even when the operator never touched
 * the field. This converts the instant to local components so the round trip
 * (seed -> submit unedited) is lossless.
 *
 * Returns '' for null/undefined/unparseable input, so it is safe to bind
 * directly to a controlled input's value.
 */
export function toDateTimeLocal(iso?: string | Date | null): string {
  if (!iso) return '';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * Format a DATE-ONLY value (a `DATE` column, e.g. a date of birth) for display,
 * without re-projecting it through UTC.
 *
 * WHY THIS EXISTS: `new Date('1950-05-03')` is parsed by the ES spec as UTC
 * midnight, so `.toLocaleDateString()` in any negative-offset timezone (all of
 * the US) renders the PREVIOUS day — 5/2/1950. A DATE column has no time and no
 * zone; it is a calendar date, and shifting it is simply wrong. On this platform
 * a date of birth is used to identity-match a vulnerable resident on the at-risk
 * registry, so an off-by-one-day display can mismatch the person a responder is
 * trying to locate.
 *
 * Accepts a bare `YYYY-MM-DD` or a full ISO timestamp (whose date part is taken
 * as-is). Returns '' for null/undefined/unparseable input.
 *
 * `options` is forwarded to `toLocaleDateString` so callers that need a
 * non-default presentation (e.g. "Tue, Aug 4") can still route through this
 * helper instead of hand-rolling a `new Date(value)` that reintroduces the
 * UTC-midnight shift.
 */
export function formatDateOnly(
  value?: string | null,
  locale?: string,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!value) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return '';
  const [, y, mo, d] = m;
  // Construct from local components so no timezone conversion can occur.
  const local = new Date(Number(y), Number(mo) - 1, Number(d));
  if (Number.isNaN(local.getTime())) return '';
  return local.toLocaleDateString(locale, options);
}
